import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Star, X, Gift, Ticket, ShoppingBag, Gamepad2, Music, Menu as MenuIcon } from 'lucide-react';
import { useCommunityAuth } from '../../context/CommunityAuthContext';
import axios from '../../utils/api';
import { API } from '../../utils/api';

const REWARDS = [
  { id: 1, title: 'Zepto \u20B9100 Off', desc: 'Flat \u20B9100 off on orders above \u20B9499', stars: 200, color: '#7C3AED', Icon: ShoppingBag, brand: 'Zepto' },
  { id: 2, title: 'Jio Recharge \u20B949', desc: '\u20B949 prepaid recharge coupon', stars: 150, color: '#2563EB', Icon: Ticket, brand: 'Jio' },
  { id: 3, title: 'Spotify 1 Week Free', desc: 'Free Spotify Premium for 7 days', stars: 300, color: '#16A34A', Icon: Music, brand: 'Spotify' },
  { id: 4, title: 'Airtel Recharge \u20B999', desc: '\u20B999 prepaid recharge voucher', stars: 250, color: '#DC2626', Icon: Ticket, brand: 'Airtel' },
  { id: 5, title: 'Zepto \u20B950 Off', desc: 'Flat \u20B950 off on any order', stars: 100, color: '#7C3AED', Icon: ShoppingBag, brand: 'Zepto' },
  { id: 6, title: 'Gaming Credits \u20B9150', desc: '\u20B9150 credits for mobile games', stars: 350, color: '#EA580C', Icon: Gamepad2, brand: 'Games' },
];

const CommunityTopHeader = ({ title, showBack = false }) => {
  const navigate = useNavigate();
  const { user, token } = useCommunityAuth();
  const points = user?.points || user?.social_score || 0;
  const [showRewards, setShowRewards] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/community/notifications/unread/count`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUnreadCount(res.data.count || 0))
      .catch(() => {});
  }, [token]);

  return (
    <><motion.div 
      className="top-header"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <motion.div 
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}
        transition={{ duration: 0.3 }}
      >
        {showBack && (
          <motion.button 
            className="header-back" 
            onClick={() => navigate(-1)} 
            data-testid="header-back" 
            style={{ marginRight: 8 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <ArrowLeft size={24} />
          </motion.button>
        )}
        <motion.div
          onClick={() => navigate('/community')}
          style={{ cursor: 'pointer' }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <img src="/logo-symbol.png" alt="socialstar" style={{ width: 32, height: 32 }} />
        </motion.div>
      </motion.div>

      <div style={{ flex: 1 }} />

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: window.innerWidth < 768 ? 6 : 8
      }}>
        <motion.div
          data-testid="stars-badge"
          onClick={() => setShowRewards(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: window.innerWidth < 768 ? 3 : 6,
            padding: window.innerWidth < 768 ? '4px 8px' : '6px 12px',
            background: 'linear-gradient(135deg, #FFF7ED, #FEF3C7)',
            borderRadius: 20,
            cursor: 'pointer',
            border: '1px solid #FCD34D'
          }}
          whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3)' }}
          whileTap={{ scale: 0.98 }}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <motion.div
            animate={{ rotate: [0, -10, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
          >
            <Star size={window.innerWidth < 768 ? 16 : 18} fill="#F59E0B" color="#F59E0B" />
          </motion.div>
          <span style={{ 
            fontSize: window.innerWidth < 768 ? 12 : 14,
            fontWeight: 700, 
            color: '#92400E',
            fontFamily: 'Inter, sans-serif'
          }}>
            {points.toLocaleString()}
          </span>
        </motion.div>

        <motion.button 
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: window.innerWidth < 768 ? 4 : 8,
            color: 'var(--text-dark)',
            position: 'relative'
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          data-testid="notifications-btn"
          onClick={() => navigate('/community/notifications')}
        >
          <svg width={window.innerWidth < 768 ? 20 : 24} height={window.innerWidth < 768 ? 20 : 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
            <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
          </svg>
          {unreadCount > 0 && (
            <span data-testid="notif-badge" style={{
              position: 'absolute', top: 2, right: 2,
              minWidth: 16, height: 16, borderRadius: 8,
              background: '#EF4444', color: '#fff',
              fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '0 4px', lineHeight: 1,
            }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </motion.button>

        {/* More menu (moved from bottom nav). Sits at the extreme right of the header. */}
        <motion.button
          onClick={() => navigate('/community/menu')}
          data-testid="header-more-menu"
          aria-label="Open menu"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: window.innerWidth < 768 ? 4 : 8,
            color: 'var(--text-dark)',
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <MenuIcon size={window.innerWidth < 768 ? 22 : 26} strokeWidth={2} />
        </motion.button>
      </div>
    </motion.div>

    {/* Rewards Modal */}
    <AnimatePresence>
      {showRewards && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => setShowRewards(false)}
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }} transition={{ type: 'spring', damping: 25 }}
            onClick={(e) => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 520, background: '#fff', borderRadius: '20px 20px 0 0', maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Gift size={20} color="#F59E0B" />
                <span style={{ fontSize: 18, fontWeight: 700, color: '#1F2937' }}>Redeem Stars</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, fontWeight: 700, color: '#92400E', background: '#FEF3C7', padding: '4px 10px', borderRadius: 12 }}>
                  <Star size={14} fill="#F59E0B" color="#F59E0B" />{points}
                </span>
                <button onClick={() => setShowRewards(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
                  <X size={20} color="#9CA3AF" />
                </button>
              </div>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {REWARDS.map((r, i) => {
                const canRedeem = points >= r.stars;
                return (
                  <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                    data-testid={`reward-${r.id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: 14, borderRadius: 14, border: '1.5px solid #F3F4F6', background: canRedeem ? '#FAFAFA' : '#F9FAFB', opacity: canRedeem ? 1 : 0.5 }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: `${r.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <r.Icon size={22} color={r.color} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>{r.title}</div>
                      <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{r.desc}</div>
                    </div>
                    <button data-testid={`redeem-${r.id}`}
                      disabled={!canRedeem}
                      style={{ padding: '6px 14px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 600, cursor: canRedeem ? 'pointer' : 'not-allowed',
                        background: canRedeem ? '#5DBFAD' : '#E5E7EB', color: canRedeem ? '#fff' : '#9CA3AF', whiteSpace: 'nowrap' }}>
                      {r.stars} <Star size={10} style={{ verticalAlign: 'middle' }} />
                    </button>
                  </motion.div>
                );
              })}
            </div>
            <div style={{ padding: '12px 16px 24px', textAlign: 'center', fontSize: 12, color: '#9CA3AF' }}>
              Earn more stars by posting and completing challenges!
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default CommunityTopHeader;
